import React from 'react';
import { Plus, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { useFragments } from '@/context/FragmentsContext';
import { useProjectContext } from '@/context/ProjectContext';
import { useToast } from '@/context/ToastContext';
import { getApiErrorMessage } from '@/api/base.api';
import {
  PIPELINE_TYPE,
  type GovernedFragmentListItem,
  type UserFragmentListItem,
} from '@/interfaces/fragments.interface';
import { logger } from '@/utils/logger';
import GovernedFragmentTag from './GovernedFragmentTag';

const DOC_PREVIEW_MAX_LENGTH = 120;

const FragmentDoc: React.FC<{ doc: string }> = ({ doc }) =>
  doc ? (
    <div className='text-xs text-muted-foreground dark:text-muted-foreground-dark mt-2 truncate'>
      {doc.length > DOC_PREVIEW_MAX_LENGTH
        ? `${doc.slice(0, DOC_PREVIEW_MAX_LENGTH)}…`
        : doc}
    </div>
  ) : null;

/** A governed pipeline is read-only: its row opens nothing and has no delete. */
const GovernedFragmentRow: React.FC<{ fragment: GovernedFragmentListItem }> = ({
  fragment,
}) => (
  <div
    className='border border-border rounded-lg p-3'
    data-testid={`fragment-row-${fragment.name}`}
  >
    <div className='flex items-start justify-between gap-2'>
      <div className='text-sm font-medium font-mono truncate'>
        {fragment.name}
      </div>
      <GovernedFragmentTag title='Governed pipeline from the domain package (read-only)' />
    </div>
    {fragment.question && (
      <div
        className='text-xs mt-2 line-clamp-2'
        title={fragment.question}
        data-testid={`fragment-question-${fragment.name}`}
      >
        {fragment.question}
      </div>
    )}
    {fragment.runtime_params.length > 0 && (
      <div className='flex flex-wrap gap-1 mt-2'>
        {fragment.runtime_params.map((param) => (
          <span
            key={param.name}
            title={param.description}
            className='px-1.5 py-0.5 rounded-full text-[10px] font-mono border bg-muted border-border'
          >
            {param.name}: {param.type}
          </span>
        ))}
      </div>
    )}
    <FragmentDoc doc={fragment.doc} />
  </div>
);

interface FragmentsSectionProps {
  /** Section collapse state, owned by the panel so entry points can focus. */
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

/**
 * Fragments section of the shared sidebar panel (see NotesPanel). Renders
 * its own collapsible header with a count badge and a "New" button; the
 * search box lives in the panel and drives `searchTerm` on FragmentsContext.
 */
const FragmentsSection: React.FC<FragmentsSectionProps> = ({
  isExpanded,
  onToggleExpanded,
}) => {
  const {
    filteredFragments,
    searchTerm,
    setCurrentEditingFragment,
    setIsEditorModalOpen,
    fetchFragmentDetail,
    removeFragment,
    isLoading,
    error,
    hasLoadedFragments,
    refetchFragments,
  } = useFragments();

  const { showToast } = useToast();
  const { selectedProjectId } = useProjectContext();

  const handleNewFragment = () => {
    setCurrentEditingFragment(null);
    setIsEditorModalOpen(true);
  };

  const handleEditFragment = async (fragment: UserFragmentListItem) => {
    try {
      const detail = await fetchFragmentDetail(fragment.name);
      // A pack installed since the list loaded made this name governed.
      if (detail.type === PIPELINE_TYPE.GOVERNED) {
        await refetchFragments();
        return;
      }
      setCurrentEditingFragment(detail);
      setIsEditorModalOpen(true);
    } catch (err) {
      logger.error('Failed to load fragment detail:', err);
      showToast(
        'error',
        'Fragment',
        getApiErrorMessage(err, 'Failed to load function')
      );
    }
  };

  const handleDeleteFragment = async (
    e: React.MouseEvent,
    fragment: UserFragmentListItem
  ) => {
    e.stopPropagation();
    if (!window.confirm(`Delete function "${fragment.name}"?`)) {
      return;
    }
    try {
      await removeFragment(fragment.name);
      showToast('success', 'Function', `Deleted "${fragment.name}"`);
    } catch (err) {
      logger.error('Failed to delete fragment:', err);
      showToast(
        'error',
        'Fragment',
        getApiErrorMessage(err, 'Failed to delete fragment')
      );
    }
  };

  return (
    <section data-testid='fragments-section'>
      {/* Section Header */}
      <div className='sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-card dark:bg-card-dark border-b border-border'>
        <button
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          className='flex items-center gap-2 p-1 -ml-1 rounded-md hover:bg-muted dark:hover:bg-selected-item-dark transition-colors'
          title={isExpanded ? 'Collapse Functions' : 'Expand Functions'}
        >
          {isExpanded ? (
            <ChevronDown className='w-4 h-4' />
          ) : (
            <ChevronRight className='w-4 h-4' />
          )}
          <h4 className='text-sm font-medium'>Functions</h4>
          <span
            className='px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-muted border border-border'
            data-testid='fragments-count'
          >
            {filteredFragments.length}
          </span>
        </button>

        <button
          onClick={handleNewFragment}
          className='flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm'
          aria-label='New fragment'
        >
          <Plus className='w-4 h-4' />
          New
        </button>
      </div>

      {/* Section Body */}
      {isExpanded && (
        <div className='p-4 space-y-2'>
          {/* Error State */}
          {error && (
            <div className='text-center py-8'>
              <div className='text-destructive text-sm mb-2'>
                Error loading fragments
              </div>
              <div className='text-xs'>{error}</div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !error && (
            <div className='text-center py-8'>
              <div className='text-sm'>Loading fragments...</div>
            </div>
          )}

          {/* Fragments Content */}
          {!isLoading && !error && !hasLoadedFragments ? (
            <div className='text-center py-8'>
              {/* The + button only opens the editor -- it never loads. The
                  panel fills both sections when it is opened, so this state
                  means either no project is selected or the project changed
                  while the panel stayed open. */}
              <div className='text-sm mb-2'>
                {selectedProjectId
                  ? 'Reopen this panel to load functions'
                  : 'Select a project to load functions'}
              </div>
            </div>
          ) : (
            !isLoading &&
            !error && (
              <>
                {filteredFragments.length > 0 ? (
                  filteredFragments.map((fragment) =>
                    fragment.type === PIPELINE_TYPE.GOVERNED ? (
                      <GovernedFragmentRow
                        key={fragment.name}
                        fragment={fragment}
                      />
                    ) : (
                      <div
                        key={fragment.name}
                        role='button'
                        tabIndex={0}
                        onClick={() => void handleEditFragment(fragment)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void handleEditFragment(fragment);
                          }
                        }}
                        aria-label={`Edit ${fragment.name}`}
                        className='group border border-border rounded-lg p-3 cursor-pointer hover:bg-muted dark:hover:bg-selected-item-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        data-testid={`fragment-row-${fragment.name}`}
                      >
                        <div className='flex items-start justify-between gap-2'>
                          <div className='text-sm font-medium font-mono truncate'>
                            {fragment.name}
                          </div>
                          <button
                            onClick={(e) =>
                              void handleDeleteFragment(e, fragment)
                            }
                            className='p-1 rounded-md text-muted-foreground dark:text-muted-foreground-dark hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shrink-0'
                            title={`Delete ${fragment.name}`}
                            aria-label={`Delete ${fragment.name}`}
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                        {fragment.params.length > 0 && (
                          <div className='flex flex-wrap gap-1 mt-2'>
                            {fragment.params.map((param) => (
                              <span
                                key={param}
                                className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono border ${
                                  fragment.required.includes(param)
                                    ? 'bg-primary/10 border-[hsl(var(--primary)/0.3)]'
                                    : 'bg-muted border-border'
                                }`}
                              >
                                {param}
                                {fragment.required.includes(param) ? ' *' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                        <FragmentDoc doc={fragment.doc} />
                      </div>
                    )
                  )
                ) : searchTerm ? (
                  <div className='text-center py-8'>
                    <div className='text-sm'>
                      No functions found matching "{searchTerm}"
                    </div>
                  </div>
                ) : (
                  <div className='text-center py-8'>
                    <div className='text-sm mb-2'>No functions yet</div>
                    <button
                      onClick={handleNewFragment}
                      className='text-sm hover:underline'
                    >
                      Create your first fragment
                    </button>
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}
    </section>
  );
};

export default FragmentsSection;
